import { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Unity, useUnityContext } from "react-unity-webgl";

import useRefreshToken from "../../hooks/useRefreshToken";
import { useAuth } from "../../hooks/useAuth";
import { axiosPrivate } from "../../api/axios";
import API_URL from "../../utils/API_URL";

import Divider from "@mui/material/Divider";
import SideBar from "../SideBar";
import SimulationChart from "./SimulationChart"; // SimulationChart 컴포넌트 임포트

import { useDispatch, useSelector } from "react-redux";
import { setChattingStatus, setSvResultStatus } from "../../slice/authSlice";

// 로딩 화면 스타일 시트
import "../../styles/loader.css";

function SVPage() {
  useEffect(() => {
    document.title = "시뮬레이션";
  }, []);
  const auth = useAuth();
  const { group_code, project_code } = useParams();
  const { svResultStatus } = useSelector((state) => state.auth); // Redux 상태 가져오기
  const { unityProvider, unload, sendMessage } = useUnityContext({
    loaderUrl: "https://dev.insightwin.com/Build/WebGL.loader.js",
    dataUrl: "https://dev.insightwin.com/Build/WebGL.data.gz",
    frameworkUrl: "https://dev.insightwin.com/Build/WebGL.framework.js.gz",
    codeUrl: "https://dev.insightwin.com/Build/WebGL.wasm.gz",
    streamingAssetsUrl: `https://dev.insightwin.com/StreamingAssets/${group_code}/${project_code}/sv`,
  });
  const dispatch = useDispatch();
  const unityContainerRef = useRef(null);
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  const [isLoading, setIsLoading] = useState(true);

  // Unity가 로딩 시작과 끝을 알려주는 함수
  const simViewTimerRef = useRef(null);
  const SFV_FinishLoading = useCallback(
    (msg) => {
      console.log(msg);
      if (msg === "Initialization start") {
        setIsLoading(false);
      } else if (msg === "Initialization done") {
        // 2층 숨기기
        // sendMessage("Canvas", "SetFloorVisible", "");
        // SimView 시작 (유니티 초기화 완료 후 타이밍 이슈 방지를 위해 1초 딜레이)
        simViewTimerRef.current = setTimeout(() => {
          sendMessage("Canvas", "SetSimViewId", "1");
        }, 2000);
      }
    },
    [sendMessage]
  );

  const SFV_UpdateData = useCallback(
    (mchId, pickingData) => {
      console.log("MchID:", mchId, "PickingData:", pickingData);

      if (mchId === "1" && pickingData.includes('"type":0')) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => {
            console.log("마이크 권한 승인");
            sendMessage("Canvas", "CurrentMicrophoneStatus", "Enable");
          })
          .catch((error) => {
            console.error("마이크 권한 획득 실패: ", error);
            sendMessage("Canvas", "CurrentMicrophoneStatus", error.message);
          });
      }
    },
    [sendMessage]
  );

  const SFV_GetSimList = useCallback(async () => {
    try {
      // API_URL(https://dev.insightwin.com/backend)에서 /backend 제거하여 루트 도메인 추출
      const rootUrl = API_URL.replace("/backend", "");

      const response = await axiosPrivate.get(
        `/StreamingAssets/${group_code}/${project_code}/sv/Api/SimInfo`,
        {
          baseURL: rootUrl, // baseURL 재정의
        }
      );

      // axios는 response.data에 JSON 결과가 들어있음. response.ok 체크 불필요(에러시 catch로 감)
      const data = response.data;
      console.log("SimInfo List:", data.simInfo);

      // Unity로 리스트 전달 (JSON 문자열)
      // Unity측 수신 함수명: "ReceiveSimList" (가정, 필요시 수정)
      sendMessage("Canvas", "ReceiveSimList", JSON.stringify(data));
    } catch (error) {
      console.error("SFV_GetSimList Error:", error);
      sendMessage(
        "Canvas",
        "ReceiveSimList",
        JSON.stringify({ error: error.message })
      );
    }
  }, [group_code, project_code, sendMessage]);

  const SFV_UpdateStatus = useCallback(
    (type, status) => {
      dispatch(
        setChattingStatus({
          type,
          status,
        })
      );
    },
    [dispatch]
  );

  const updateCanvasSize = () => {
    const unityContainer = unityContainerRef.current;
    if (unityContainer) {
      const canvas = unityContainer.querySelector("canvas");
      if (canvas) {
        const newWidth = unityContainer.clientWidth;
        const newHeight = unityContainer.clientHeight;
        const resolutionWidth = newWidth * window.devicePixelRatio;
        const resolutionHeight = newHeight * window.devicePixelRatio;

        // 유니티 WebGL 내부 해상도 업데이트
        canvas.width = resolutionWidth;
        canvas.height = resolutionHeight;
      }
    }
  };

  const handleLogout = async () => {
    await unload();
    await auth.logout();
  };

  // Refresh Token 갱신
  const refresh = useRefreshToken();

  // Refresh Token 갱신
  useEffect(() => {
    let refreshInterval;

    const setupRefreshInterval = () => {
      if (refreshInterval) {
        console.log("Clearing existing interval");
        clearInterval(refreshInterval);
      }

      refreshInterval = setInterval(async () => {
        try {
          console.log("토큰 갱신중");
          await refresh(); // Refresh Token 갱신
          console.log("토큰 갱신 완료");
        } catch (error) {
          console.error("Failed to refresh tokens:", error);
          await unload();
          await auth.logout();
        }
      }, 12 * 60 * 60 * 1000);
    };

    setupRefreshInterval();

    return () => {
      console.log("Clearing interval on cleanup");
      clearInterval(refreshInterval); // 컴포넌트 언마운트 시 정리
    };
  }, [refresh, auth, unload]);

  useEffect(() => {
    const unityContainer = unityContainerRef.current;

    const handleDevicePixelRatioChange = () => {
      // console.log("Device Pixel Ratio:", window.devicePixelRatio);
      setDevicePixelRatio(window.devicePixelRatio);
      updateCanvasSize();
    };

    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${window.devicePixelRatio}dppx)`
    );
    mediaMatcher.addEventListener("change", handleDevicePixelRatioChange);

    updateCanvasSize();

    const observer = new ResizeObserver(() => {
      updateCanvasSize();
    });

    if (unityContainer) {
      observer.observe(unityContainer);
    }

    return () => {
      mediaMatcher.removeEventListener("change", handleDevicePixelRatioChange);
      if (unityContainer) {
        observer.unobserve(unityContainer);
      }
    };
  }, [devicePixelRatio]);

  useEffect(() => {
    window.SFV_UpdateData = SFV_UpdateData;
    window.SFV_UpdateStatus = SFV_UpdateStatus;
    window.SFV_FinishLoading = SFV_FinishLoading;
    window.SFV_GetSimList = SFV_GetSimList;
    return () => {
      delete window.SFV_UpdateData;
      delete window.SFV_UpdateStatus;
      delete window.SFV_FinishLoading;
      delete window.SFV_GetSimList;
      // SimView 타이머 정리
      if (simViewTimerRef.current) {
        clearTimeout(simViewTimerRef.current);
        simViewTimerRef.current = null;
      }
    };
  }, [SFV_UpdateData, SFV_UpdateStatus, SFV_FinishLoading, SFV_GetSimList]);

  useEffect(() => {
    const preventDefault = (event) => {
      event.preventDefault();
    };

    const bodyElement = document.body;

    bodyElement.addEventListener("contextmenu", preventDefault);
    bodyElement.addEventListener("dragstart", preventDefault);
    bodyElement.addEventListener("selectstart", preventDefault);

    return () => {
      bodyElement.removeEventListener("contextmenu", preventDefault);
      bodyElement.removeEventListener("dragstart", preventDefault);
      bodyElement.removeEventListener("selectstart", preventDefault);
    };
  }, []);

  return (
    <div id="sfv-page">
      <SideBar pageType="SV" handleLogout={handleLogout} />
      <div
        ref={unityContainerRef}
        style={{
          width: "calc(100vw - 80px)",
          height: "100vh",
          overflow: "hidden",
          marginLeft: "80px",
          position: "relative", // 차트 컴포넌트의 기준점을 위해 추가
        }}
      >
        <Unity
          unityProvider={unityProvider}
          style={{ width: "100%", height: "100%" }}
          // 중요한 옵션, 디폴트가 true인데 WebGL 해상도를 캔버스 사이즈에 강제로 일치시킴
          matchWebGLToCanvasSize={false}
        />

        {/* 로딩 화면 */}
        {isLoading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <img
              src="/images/ese_logo.png"
              alt="ESE Logo"
              style={{
                width: "200px",
                marginBottom: "30px",
              }}
            />
            <div className="loader"></div>
          </div>
        )}
        <Divider />
        {svResultStatus && <SimulationChart sendMessage={sendMessage} />}
      </div>
    </div>
  );
}

export default SVPage;
